/**
 * Calibration API Service
 */
import { apiClient } from './base';

// TypeScript Interfaces

export interface Calibration {
  /** From the calibration pour; null for Manual calibrations. */
  maturity_basis?: MaturityBasis | null;
  specimens?: CalibrationSpecimen[];
  id: string;
  code: string;
  name?: string;
  company_id?: string;
  batched_time: string;
  sampled_time: string;
  cast_time?: string;
  create_date: string;
  load_size?: string;
  docket_number?: string;
  target_workability?: string;
  actual_workability?: string;
  air_content?: string;
  fresh_density?: string;
  sampled_location?: string;
  ambient_temp?: string;
  concrete_temp?: string;
  sampling_method?: string;
  specimen_cast_no?: string;
  specimen1?: string;
  specimen2?: string;
  specimen3?: string;
  specimen4?: string;
  calibration_type?: string;
  comment?: string;
  regression_a?: number;
  regression_b?: number;
  curve_type?: CurveType;
  linear_a?: number | null;
  linear_b?: number | null;
  r2_log?: number | null;
  r2_linear?: number | null;
  pour_id?: string;
  user_id?: string;
  notification_contact?: string;
  speciment1_accept?: boolean;
  speciment2_accept?: boolean;
  expected_cast_time?: string;
  calibration_verification_id?: string;
  /** Root (original) calibration ID when this is a later version. */
  parent_calibration_id?: string | null;
  /** Version number; null/undefined means v1. */
  version_no?: number | null;
  /** Root calibration's code; single-GET only. */
  parent_code?: string | null;
  /** True while any product link is Finished (Status=2); single-GET only. */
  is_locked?: boolean;
  /** ASTM C1074 9.2 basis: snapshot, calibration pour, chosen equation or the mix design's. */
  basis?: CalibrationBasis | null;
  /** Manual calibrations: the maturity equation the typed maturities were read with. */
  maturity_equation_id?: string | null;
  /** ASTM C1074 checks report; single-GET only. */
  checks?: CalibrationChecks | null;
  /** Calibration pour summary; single-GET only. */
  pour?: CalibrationPourSummary | null;
  /** The database has the calibration maturity-equation and third-cylinder columns. */
  supports_basis?: boolean;
  /** Pours whose strength comes from this calibration; any keeps it locked. */
  used_by_pours?: number;
  supports_third_cylinder?: boolean;
  /** Create only: the mix design to link to. */
  product_id?: string;
}

/** GET /products/calibrations/{id}/removal */
export interface CalibrationRemovalCheck {
  calibration_id: string;
  code: string;
  name?: string | null;
  can_delete: boolean;
  /** Why it cannot be deleted, each with what to do instead. */
  blockers: string[];
  used_by_pours: number;
  test_results: number;
  linked_mixes: { product_id: string; code?: string | null; is_active: boolean }[];
}

export interface CalibrationBasis {
  /** 'snapshot' | 'calibration_pour' | 'equation' | 'mix_design' (assumed). */
  source: string;
  method: 'Nurse Saul' | 'Arrhenius' | string;
  unit: string;
  datum_temp: number | null;
  datum_is_default?: boolean;
  activation_energy: number | null;
  q_value: number | null;
  q_is_default?: boolean;
  specified_temp: number | null;
  equation_id?: string | null;
  equation_name?: string | null;
  pour_id?: string | null;
  pour_name?: string | null;
}

export type CheckStatus = 'ok' | 'warn' | 'fail' | 'na';

export interface CalibrationCheck {
  rule: string;
  clause: string;
  status: CheckStatus;
  title: string;
  detail: string;
}

export interface CalibrationChecks {
  checks: CalibrationCheck[];
  has_fail: boolean;
  has_warn: boolean;
  ages_tested: number;
  ages_recommended: number;
  maturity_min: number | null;
  maturity_max: number | null;
  r2: number | null;
  basis: CalibrationBasis | null;
  mix_basis: CalibrationBasis | null;
  mix_comparison: { status: 'match' | 'mismatch' | 'differs' | 'unknown'; differences: string[] };
}

export interface CalibrationPourSummary {
  pour_id: string;
  pour_name: string | null;
  pour_code: string | null;
  status?: string | null;
  /** Naive UTC ISO (no Z). */
  concrete_start_utc: string | null;
}

export interface SpecimenSeries {
  slot: number;
  location_id: string;
  name: string;
  logger_code?: string | null;
  channel_no?: number | null;
  accepted: boolean;
  start?: string | null;
  /** [naive UTC ISO time, temperature °C, maturity] */
  points: [string, number, number][];
}

export interface TemperatureMaturityData {
  pour_id: string | null;
  pour_name?: string | null;
  pour_code?: string | null;
  unit: string | null;
  series: SpecimenSeries[];
}

/** Maturity function + parameter the sample maturities were computed with (from the calibration pour). */
export interface MaturityBasis {
  source: string;
  pour_id: string;
  pour_name: string;
  method: 'Nurse Saul' | 'Arrhenius' | string;
  /** '°C·hrs' for Nurse-Saul, 'hrs' for Arrhenius. */
  unit: string;
  datum_temp: number | null;
  datum_is_default?: boolean;
  activation_energy: number | null;
  q_value: number | null;
  q_is_default?: boolean;
  specified_temp: number | null;
}

export interface CalibrationSpecimen {
  slot: number;
  location_id: string;
  name: string;
  logger_code?: string | null;
  channel_no?: number | null;
  accepted: boolean;
}

export interface CalibrationPourOption {
  pour_id: string;
  pour_name: string;
  pour_code?: string | null;
  status?: string | null;
  started?: string | null;
  maturity_method?: string | null;
  locations: { id: string; name: string; logger_code?: string | null; channel_no?: number | null }[];
}

export interface CalibrationListItem {
  id: string;
  code: string;
  name?: string;
  company_id?: string;
  batched_time: string;
  sampled_time: string;
  create_date: string;
  calibration_type?: string;
  regression_a?: number;
  regression_b?: number;
  parent_calibration_id?: string | null;
  version_no?: number | null;
}

export interface CalibrationSample {
  /** Test time in UTC. System calibrations read the sensors at this instant. */
  sampled_time_utc?: string;
  id: string;
  calibration_id?: string;
  avg_strength?: number;
  sampled_time?: string;
  density?: string;
  is_exclude?: boolean;
  strength1?: number;
  is_exclude_strength1?: boolean;
  strength2?: number;
  is_exclude_strength2?: boolean;
  /** ASTM C1074 8.4 third cylinder (range of the first two over 10 %). */
  strength3?: number | null;
  is_exclude_strength3?: boolean | null;
  clear_strength3?: boolean;
  /** Hours from the concrete start (batched time). */
  age_hours?: number | null;
  /** Range of cylinders 1 and 2 as a % of their average. */
  pair_range_pct?: number | null;
  needs_third_cylinder?: boolean;
  channel1_temp?: number;
  channel2_temp?: number;
  channel3_temp?: number;
  channel4_temp?: number;
  channel1_ea?: number;
  channel2_ea?: number;
  channel3_ea?: number;
  channel4_ea?: number;
  avg_temp?: number;
  avg_ea?: number;
  calculated_strength?: number;
}

export type CurveType = 'log' | 'linear';

export interface RegressionResult {
  regression_a: number;
  regression_b: number;
  sample_count: number;
  r2_log?: number | null;
  linear_a?: number | null;
  linear_b?: number | null;
  r2_linear?: number | null;
  recommended_curve?: CurveType | null;
  curve_type?: CurveType | null;
}

export interface CalibrationStatusResponse {
  calibration_id: string;
  status: string;
  message: string;
}

export interface GraphData {
  samples: number[][];
  regression_a?: number | null;
  regression_b?: number | null;
  linear_a?: number | null;
  linear_b?: number | null;
  r2_log?: number | null;
  r2_linear?: number | null;
  curve_type: CurveType;
  curve_type_is_manual: boolean;
  recommended_curve?: CurveType | null;
  extend_start_pct: number;
  extend_end_pct: number;
  min_ea?: number;
  max_ea?: number;
}

export interface CurveSettingsUpdate {
  curve_type?: CurveType;
  extend_start_pct?: number;
  extend_end_pct?: number;
}

export interface CurveSettingsResponse {
  calibration_id: string;
  curve_type: CurveType;
  curve_type_is_manual: boolean;
  extend_start_pct: number;
  extend_end_pct: number;
}

// API Service
const calibrationsService = {
  // Calibration CRUD
  list: async (companyId?: string): Promise<CalibrationListItem[]> => {
    const params = companyId ? { company_id: companyId } : {};
    const response = await apiClient.get('/products/calibrations', { params });
    return response.data;
  },

  get: async (calId: string): Promise<Calibration> => {
    const response = await apiClient.get(`/products/calibrations/${calId}`);
    return response.data;
  },

  /** Maturity pours (with concrete sensor locations) that can serve as a calibration pour. */
  getPourOptions: async (): Promise<CalibrationPourOption[]> => {
    const response = await apiClient.get('/products/calibrations/pour-options');
    return response.data;
  },

  /** Choose (or clear, with an empty pourId) the calibration pour and its instrumented specimens. */
  setCalibrationPour: async (calId: string, pourId: string, locationIds: string[]): Promise<Calibration> => {
    const response = await apiClient.put(`/products/calibrations/${calId}/calibration-pour`, {
      pour_id: pourId || null,
      location_ids: locationIds,
    });
    return response.data;
  },

  create: async (data: Partial<Calibration>): Promise<Calibration> => {
    const response = await apiClient.post('/products/calibrations', data);
    return response.data;
  },

  update: async (calId: string, data: Partial<Calibration>): Promise<Calibration> => {
    const response = await apiClient.put(`/products/calibrations/${calId}`, data);
    return response.data;
  },

  /** What deleting would remove, and what stops it (same rules as delete). */
  removalCheck: async (calId: string): Promise<CalibrationRemovalCheck> => {
    const response = await apiClient.get(`/products/calibrations/${calId}/removal`);
    return response.data;
  },

  delete: async (calId: string): Promise<void> => {
    await apiClient.delete(`/products/calibrations/${calId}`);
  },

  // Samples
  listSamples: async (calId: string): Promise<CalibrationSample[]> => {
    const response = await apiClient.get(`/products/calibrations/${calId}/samples`);
    return response.data;
  },

  createSample: async (calId: string, data: Partial<CalibrationSample>): Promise<CalibrationSample> => {
    const response = await apiClient.post(`/products/calibrations/${calId}/samples`, data);
    return response.data;
  },

  updateSample: async (calId: string, sampleId: string, data: Partial<CalibrationSample>): Promise<CalibrationSample> => {
    const response = await apiClient.put(`/products/calibrations/${calId}/samples/${sampleId}`, data);
    return response.data;
  },

  deleteSample: async (calId: string, sampleId: string): Promise<void> => {
    await apiClient.delete(`/products/calibrations/${calId}/samples/${sampleId}`);
  },

  // Workflow
  calculateRegression: async (calId: string): Promise<RegressionResult> => {
    const response = await apiClient.post(`/products/calibrations/${calId}/calculate-regression`);
    return response.data;
  },

  /** With acknowledgeWarnings false, ASTM warnings return 409 with the checks. */
  finish: async (calId: string, acknowledgeWarnings = true): Promise<CalibrationStatusResponse> => {
    const response = await apiClient.post(`/products/calibrations/${calId}/finish`, {
      acknowledge_warnings: acknowledgeWarnings,
    });
    return response.data;
  },

  getChecks: async (calId: string, productId?: string): Promise<CalibrationChecks> => {
    const params = productId ? { product_id: productId } : {};
    const response = await apiClient.get(`/products/calibrations/${calId}/checks`, { params });
    return response.data;
  },

  /** Temperature and maturity history of each sensor cylinder (System calibrations). */
  getTemperatureMaturity: async (calId: string): Promise<TemperatureMaturityData> => {
    const response = await apiClient.get(`/products/calibrations/${calId}/temperature-maturity`);
    return response.data;
  },

  /**
   * Clone a finished calibration into a new draft version. The current
   * version stays finished and active on its products; the draft is applied
   * via Finish + Activate. Replaces the legacy in-place unlock.
   */
  newVersion: async (calId: string): Promise<Calibration> => {
    const response = await apiClient.post(`/products/calibrations/${calId}/new-version`);
    return response.data;
  },

  activate: async (calId: string, productId: string): Promise<CalibrationStatusResponse> => {
    const response = await apiClient.post(`/products/calibrations/${calId}/activate`, { product_id: productId });
    return response.data;
  },

  // Graph
  getGraphData: async (calId: string): Promise<GraphData> => {
    const response = await apiClient.get(`/products/calibrations/${calId}/graph-data`);
    return response.data;
  },

  updateCurveSettings: async (calId: string, data: CurveSettingsUpdate): Promise<CurveSettingsResponse> => {
    const response = await apiClient.put(`/products/calibrations/${calId}/curve-settings`, data);
    return response.data;
  },
};

export default calibrationsService;
